import React from 'react';
import clsx from 'clsx';
import useCheckoutState from '../hooks/useCheckoutState';
import Badge from './Badge';
import { formatPrice } from '../services/formatPrice';
import { Select } from './Form';
import getVisibleTicketTypes from '../lib/getVisibleTicketTypes';
import getBulkDiscount from '../lib/getBulkDiscount';
import { getImageSrcAndSrcSet } from '../services/getUrlFromCdn';
import TicketTypeSaleDate from './TicketTypeSaleDate';
import { shouldShowTicketSaleDates } from '../lib/ticketAvailabilityHelpers';
import checkIsTicketTypeNotOnSale from '../lib/checkIsTicketTypeNotOnSale';
import checkIsTicketGroupNotOnSale from '../lib/checkIsTicketGroupNotOnSale';

function getTicketTypeBulkDiscountAmount(ticketType, quantity, subtotal, bulkDiscounts = []) {
  const bulkDiscountsForTicketType = bulkDiscounts.filter(
    discount =>
      discount.quantityCondition === 'ticketTypeQuantity' &&
      discount.ticketTypes.includes(ticketType._id),
  );

  const { bulkDiscountAmount } = getBulkDiscount(bulkDiscountsForTicketType, quantity, subtotal);

  return bulkDiscountAmount;
}

export default function TicketType({
  ticketType,
  selectedQuantity,
  isSelected,
  hasQuantitySelect,
  onQuantitySelect,
  ticketGroup,
  isSoldOut,
  isCanceled,
  isUnavailable,
  isOnSale,
  isTicketTypeScheduled,
  ticketTypeHasInvalidReference,
  isTicketGroupScheduled,
  ticketGroupHasInvalidReference,
}) {
  const { page, query } = useCheckoutState();

  const allTicketTypes = page.ticketGroups?.flatMap(group => group.ticketTypes) || [];
  const allTicketGroups = page.ticketGroups || [];

  const visibleTicketTypes = getVisibleTicketTypes({ page, ticketGroup, query });
  const index = visibleTicketTypes.indexOf(ticketType);

  const showAvailableQuantity =
    !isCanceled &&
    !isSoldOut &&
    !isUnavailable &&
    ticketType.showAvailableQuantity &&
    ticketType.hasCapacity;

  const isScheduled = isTicketTypeScheduled || isTicketGroupScheduled;
  const hasInvalidReference = ticketTypeHasInvalidReference || ticketGroupHasInvalidReference;

  const isTicketGroupNotOnSale = checkIsTicketGroupNotOnSale({
    ticketGroup,
    isCanceled,
    isOnSale: ticketGroup.isOnSale,
    isScheduled: isTicketGroupScheduled,
    hasInvalidReference: ticketGroupHasInvalidReference,
  });

  const isTicketTypeNotOnSale = checkIsTicketTypeNotOnSale({
    ticketType,
    isCanceled,
    isOnSale: ticketType.isOnSale,
    isScheduled: isTicketTypeScheduled,
    hasInvalidReference: ticketTypeHasInvalidReference,
  });

  const isNotOnsale = isTicketTypeNotOnSale || isTicketGroupNotOnSale;

  const showTicketSaleDates = shouldShowTicketSaleDates(
    ticketType,
    ticketGroup,
    isCanceled,
    isSoldOut,
    isUnavailable,
    isOnSale,
  );

  const price = ticketType.price;
  const quantity = selectedQuantity;
  const subtotal = price * quantity;
  const bulkDiscountAmount = getTicketTypeBulkDiscountAmount(
    ticketType,
    quantity,
    subtotal,
    page.eventDetails.discounts,
  );
  const ticketTypeTotal = subtotal - bulkDiscountAmount;
  const ticketTypePrice = ticketTypeTotal > 0 ? ticketTypeTotal / quantity : ticketType.price;

  return (
    <div
      value={ticketType._id}
      className={clsx(
        'cp_ticket-type-wrapper relative focus:outline-none h-full border border-gray-200 bg-[var(--ticket-type-background-color)]',
        {
          'p-2': ticketGroup.layout?.spacing === 'compact',
          'p-4': ticketGroup.layout?.spacing === 'comfortable' || !ticketGroup.layout?.spacing,
          'px-2': ticketGroup.layout?.type === 'grid' && ticketGroup.layout?.columns > 3,
          'rounded-md': ticketGroup.layout?.type === 'grid',
          'rounded-tl-md rounded-tr-md': index === 0,
          'rounded-bl-md rounded-br-md': index === visibleTicketTypes.length - 1,
          'shadow-input': isSelected,
          [`cp_ticket-type--selected z-10 ring-1 ring-[var(--ticket-type-selected-border-color)] !border-[var(--ticket-type-selected-border-color)] !bg-[var(--ticket-type-selected-background-color)]`]:
            isSelected,
        },
      )}
    >
      <div
        className={clsx('cp_ticket-type flex flex-col flex-nowrap w-full', {
          'text-left':
            ticketGroup.layout?.alignment === 'default' ||
            ticketGroup.layout?.alignment === 'stack',
          'text-center': ticketGroup.layout?.alignment === 'center',
        })}
      >
        <div
          className={clsx('flex flex-nowrap w-full gap-x-2', {
            'text-left': ticketGroup.layout?.alignment === 'default',
            'flex-col text-left': ticketGroup.layout?.alignment === 'stack',
            'flex-col text-center': ticketGroup.layout?.alignment === 'center',
            'space-y-2':
              ticketGroup.layout?.alignment === 'stack' ||
              ticketGroup.layout?.alignment === 'center',
          })}
        >
          {ticketType.image && (
            <TicketTypeImage ticketType={ticketType} ticketGroup={ticketGroup} />
          )}

          <div className={clsx('w-full')}>
            {ticketGroup.layout?.showTicketTypeName && <TicketTypeName ticketType={ticketType} />}

            <TicketTypePrice
              ticketTypePrice={ticketTypePrice}
              ticketType={ticketType}
              ticketGroup={ticketGroup}
            />

            {showAvailableQuantity && (
              <div className="cp_ticket-type-available-quantity text-gray-500 text-sm">
                {ticketType.remainingCapacity} {page.ticketsRemainingText}
              </div>
            )}

            {showTicketSaleDates && (
              <TicketTypeSaleDate
                page={page}
                ticketGroup={ticketGroup}
                ticketType={ticketType}
                allTicketTypes={allTicketTypes}
                allTicketGroups={allTicketGroups}
              />
            )}
          </div>

          {(() => {
            // Determine if we should show any badges or quantity select
            const shouldShowContent =
              isSoldOut ||
              hasQuantitySelect ||
              isOnSale ||
              isCanceled ||
              isUnavailable ||
              isScheduled ||
              isNotOnsale;

            if (!shouldShowContent) return null;

            return (
              <div
                className={clsx('flex flex-col gap-y-2', {
                  'ml-auto pl-4': ticketGroup.layout?.alignment === 'left',
                  'mx-auto': ticketGroup.layout?.alignment === 'center',
                  'mr-auto': ticketGroup.layout?.alignment === 'stack',
                })}
              >
                {isCanceled ? (
                  <TicketTypeCanceled />
                ) : page.eventDetails?.status === 'sold_out' ? (
                  <TicketTypeSoldOut />
                ) : ticketGroup.availabilityStatus === 'unavailable' ? (
                  <TicketTypeUnavailable />
                ) : isTicketGroupNotOnSale ? (
                  <TicketTypeNotOnSale />
                ) : isSoldOut ? (
                  <TicketTypeSoldOut />
                ) : isUnavailable ? (
                  <TicketTypeUnavailable />
                ) : isNotOnsale || hasInvalidReference ? (
                  <TicketTypeNotOnSale />
                ) : isOnSale ? null : isScheduled ? (
                  <TicketTypeOnSaleSoon />
                ) : null}

                {hasQuantitySelect && !isSoldOut && !isUnavailable && isOnSale && !isCanceled && (
                  <TicketTypeQuantitySelect
                    ticketType={ticketType}
                    selectedQuantity={selectedQuantity}
                    onQuantitySelect={onQuantitySelect}
                  />
                )}

                {ticketTypeTotal > 0 && (
                  <TicketTypeTotal ticketTypeTotal={ticketTypeTotal} ticketGroup={ticketGroup} />
                )}
              </div>
            );
          })()}
        </div>

        <TicketTypeDescription ticketType={ticketType} />
      </div>
    </div>
  );
}

function TicketTypeImage({ ticketType, ticketGroup }) {
  const { isPreview } = useCheckoutState();
  const { width, height } = (() => {
    switch (ticketGroup.layout?.imageSize) {
      case 'small':
        return { width: 64, height: 64 };
      case 'medium':
        return { width: 128, height: 128 };
      case 'large':
      default:
        return { width: 256, height: 256 };
    }
  })();

  const image = getImageSrcAndSrcSet({
    url: ticketType.image?.url,
    width,
    height,
    isPreview,
  });

  return (
    <div
      className={clsx(
        'cp_ticket-type-image-wrapper max-w-full rounded overflow-hidden flex-shrink-0',
        {
          'mx-auto': ticketGroup.layout?.alignment === 'center',
          'w-16 h-16': ticketGroup.layout?.imageSize === 'small',
          'w-32 h-32': ticketGroup.layout?.imageSize === 'medium',
          'w-64 h-64': ticketGroup.layout?.imageSize === 'large',
        },
      )}
    >
      {image && (
        <img
          src={image.src}
          srcSet={image.srcSet}
          className={clsx('cp_ticket-type-image object-cover', {
            'w-16 h-16': ticketGroup.layout?.imageSize === 'small',
            'w-32 h-32': ticketGroup.layout?.imageSize === 'medium',
            'w-64 h-64': ticketGroup.layout?.imageSize === 'large',
          })}
        />
      )}
    </div>
  );
}

function TicketTypeName({ ticketType }) {
  return <div className="cp_ticket-type-name font-semibold">{ticketType.name}</div>;
}

function TicketTypeDescription({ ticketType }) {
  return (
    <div className="cp_ticket-type-description text-gray-700 text-sm pt-2">
      {ticketType.description}
    </div>
  );
}

function TicketTypePrice({ ticketTypePrice, ticketType, ticketGroup }) {
  const { page } = useCheckoutState();
  const { eventDetails, locale } = page;
  const { currency } = eventDetails;

  const showPrice = ticketGroup.layout?.showTicketPrice;

  // if the ticket type has a discountedFromPrice, use it
  // otherwise, if the ticket type price is less than the ticket type price, use the ticket type price as the discounted from price
  // this would happen when there are bulk discounts applied to the ticket type
  const discountedFromPrice = ticketType.discountedFromPrice
    ? ticketType.discountedFromPrice
    : ticketTypePrice < ticketType.price
      ? ticketType.price
      : '';

  if (showPrice) {
    return (
      <div className="cp_ticket-type-price-wrapper inline-flex gap-x-1">
        {discountedFromPrice && (
          <div className="cp_ticket-type-discounted-from text-gray-600 line-through">
            {formatPrice({
              amount: discountedFromPrice,
              currency,
              locale,
            })}
          </div>
        )}

        {showPrice && (
          <div className="cp_ticket-type-price">
            {ticketType.pricing === 'paid' && ticketTypePrice > 0
              ? formatPrice({
                  amount: ticketTypePrice,
                  currency,
                  locale,
                })
              : 'Free'}
          </div>
        )}
      </div>
    );
  }

  return null;
}

function TicketTypeSoldOut() {
  const { page } = useCheckoutState();

  return (
    <Badge className="cp_sold-out cp_ticket-type-soldout bg-gray-900 text-white whitespace-nowrap">
      {page.soldOutText}
    </Badge>
  );
}

function TicketTypeCanceled() {
  const { page } = useCheckoutState();

  return (
    <Badge className="cp_canceled cp_ticket-type-canceled bg-red-500 text-white whitespace-nowrap">
      {page.canceledText}
    </Badge>
  );
}

function TicketTypeUnavailable() {
  const { page } = useCheckoutState();

  return (
    <Badge className="cp_unavailable cp_ticket-type-unavailable bg-gray-500 text-white whitespace-nowrap">
      {page.unavailableText || 'Unavailable'}
    </Badge>
  );
}

function TicketTypeOnSaleSoon() {
  const { page } = useCheckoutState();

  return (
    <Badge className="cp_on-sale-soon cp_ticket-type-on-sale-soon bg-green-300 text-white whitespace-nowrap">
      {page.onSaleSoonText || 'On sale soon'}
    </Badge>
  );
}

function TicketTypeNotOnSale() {
  const { page } = useCheckoutState();

  return (
    <Badge className="cp_not-on-sale cp_ticket-type-not-on-sale bg-gray-500 text-white whitespace-nowrap">
      {page.notOnSaleText || 'Not on sale'}
    </Badge>
  );
}

function TicketTypeTotal({ ticketTypeTotal, ticketGroup }) {
  const { page } = useCheckoutState();

  const showTotalPrice = ticketGroup.layout?.showTicketTotalPrice;

  if (!showTotalPrice) return null;

  return (
    <div className="cp_ticket-type-total whitespace-nowrap text-right text-gray-700 text-sm">
      {page.ticketTypeTotalText}{' '}
      {formatPrice({
        amount: ticketTypeTotal,
        currency: page.eventDetails.currency,
        locale: page.eventDetails.locale,
      })}
    </div>
  );
}

function TicketTypeQuantitySelect({ ticketType, selectedQuantity, onQuantitySelect }) {
  const minQuantity = ticketType.minQuantity;
  const maxQuantity = ticketType.maxQuantity;

  return (
    <div className={clsx('cp_ticket-type-quantity-wrapper')}>
      <Select
        className="cp_ticket-type-quantity-select min-w-[72px]"
        id={ticketType._id}
        name={ticketType._id}
        value={selectedQuantity}
        onChange={event => {
          onQuantitySelect(Number(event.target.value));
        }}
        required={false}
      >
        {minQuantity > 0 && <option value={0}>0</option>}

        {Array.from(
          {
            length: maxQuantity - minQuantity + 1,
          },
          (_, i) => minQuantity + i,
        ).map(quantity => (
          <option key={quantity} value={quantity}>
            {quantity}
          </option>
        ))}
      </Select>
    </div>
  );
}
